import React, { useCallback, useContext, useState } from "react";
import { partition } from "lodash";
import { SEARCH_RESULT_TYPE } from "common/constants";
import { displayForResult } from "common-client/utils/search";
import { OmniboxSearchResult } from "../../generated/graphql";
import { spacing } from "../../stitches.config";
import { FlexColumn, FlexRow } from "../Common/__styles__/Layout";
import { EmptyState } from "../Common/EmptyState";
import { IconNames } from "../Common/Icons/LucideIcons";
import SmallLoading from "../Common/Icons/SmallLoading";
import { Tag } from "../Common/Tag";
import { Body } from "../Common/Typography";
import { SelectionCard } from "../Inputs/SelectionCard";
import { NoSearchResultsContent } from "./__styles__/OmniboxSearch";
import { SearchContext } from "./SearchContext";

const OmniboxResultItem = ({
  result,
  hoveredOnMap,
}: {
  result: OmniboxSearchResult;
  hoveredOnMap?: boolean;
}) => {
  const [hoveredInResults, setHoveredInResults] = useState(hoveredOnMap);
  const { searchResultsDispatch } = useContext(SearchContext);
  const { title, description, iconName, type } = displayForResult({ result });

  const onResultHover = useCallback(
    (result: OmniboxSearchResult) => {
      setHoveredInResults(true);
      searchResultsDispatch({
        type: "setHoveredSearchResult",
        feature: {
          id: result.id,
          featureType:
            result.__typename === "ParcelSearchResult" ?
              SEARCH_RESULT_TYPE.PARCEL
            : SEARCH_RESULT_TYPE.PROPERTY,
        },
        hoverSource: "results",
      });
    },
    [searchResultsDispatch],
  );

  const onResultLeave = useCallback(() => {
    setHoveredInResults(false);
    searchResultsDispatch({
      type: "clearHoveredSearchResult",
    });
  }, [searchResultsDispatch]);

  const isHovered = hoveredInResults || hoveredOnMap;

  return (
    <SelectionCard.Item
      key={result.id}
      size="compact"
      borderStyle="ghost"
      value={result}
      onMouseEnter={() => {
        onResultHover(result);
      }}
      onMouseLeave={() => {
        onResultLeave();
      }}
      hover={isHovered}
    >
      <FlexRow gap="m">
        <SelectionCard.CommonContent
          iconName={iconName as IconNames}
          title={title}
          description={description}
        />
        <div style={{ opacity: isHovered ? 1 : 0 }}>
          <Tag styleVariant="default" size="in-line">
            {type}
          </Tag>
        </div>
      </FlexRow>
    </SelectionCard.Item>
  );
};

const OmniboxResultsLoading = ({ expand }: { expand: boolean }) => {
  return expand ?
      <FlexColumn
        data-testid="omnibox-results-loading"
        gap="m"
        style={{
          paddingTop: spacing["3xl"].value,
          alignItems: "center",
        }}
      >
        <SmallLoading />
        <Body type="emphasis" size="default" color="contentPlaceholder">
          Loading...
        </Body>
      </FlexColumn>
    : <FlexColumn
        data-testid="omnibox-results-loading"
        style={{ alignItems: "center", padding: `${spacing.xs} 0` }}
      >
        <SmallLoading />
      </FlexColumn>;
};

export const OmniboxResults = ({
  expandPlaceholders,
}: {
  expandPlaceholders: boolean;
}) => {
  const { searchResultsState, handleSearchResultClick } =
    useContext(SearchContext);

  const isHoveredOnMap = useCallback(
    (result: OmniboxSearchResult) => {
      const hoveredSearchResult = searchResultsState.hoveredSearchResult;
      if (!hoveredSearchResult) {
        return false;
      }
      return (
        hoveredSearchResult.feature.id === result.id &&
        hoveredSearchResult.feature.featureType === result.__typename &&
        hoveredSearchResult.hoverSource === "map"
      );
    },
    [searchResultsState.hoveredSearchResult],
  );

  if (
    !searchResultsState.debouncedQuery ||
    searchResultsState.debouncedQuery.length < 3
  ) {
    return <NoSearchResultsContent />;
  } else if (searchResultsState.loading) {
    return <OmniboxResultsLoading expand={expandPlaceholders} />;
  } else if (!searchResultsState.results?.length) {
    return expandPlaceholders ?
        <EmptyState
          icon="missing-location"
          paddingTop={spacing["2xl"].value}
          title="No results found"
          body="Check the spelling or search by street, city, or ZIP, then use the map to explore nearby."
        />
      : <EmptyState
          compact
          title="No results found"
          paddingTop={0}
          paddingBottom={0}
        />;
  }

  const [strictMatches, fuzzyMatches] = partition(
    searchResultsState.results,
    result => result.strictMatch,
  );

  return (
    <SelectionCard.Group
      data-testid="omnibox-results"
      direction="vertical"
      spacing="compact"
      handleSelection={handleSearchResultClick}
    >
      {strictMatches.length > 0 && (
        <Body
          type="emphasis"
          size="small"
          style={{ padding: `${spacing.s} ${spacing.m} 0` }}
        >
          {searchResultsState.isTextSearch ? "Exact Matches" : "Nearby Results"}
        </Body>
      )}
      {strictMatches.map(result => (
        <OmniboxResultItem
          result={result}
          key={result.id}
          hoveredOnMap={isHoveredOnMap(result)}
        />
      ))}
      {fuzzyMatches.length > 0 && (
        <>
          <Body
            type="emphasis"
            size="small"
            style={{
              paddingTop: strictMatches.length > 0 ? spacing.m.value : 0,
              paddingLeft: spacing.m.value,
              paddingRight: spacing.m.value,
            }}
          >
            Related Results
          </Body>
          {fuzzyMatches.map(result => (
            <OmniboxResultItem
              result={result}
              key={result.id}
              hoveredOnMap={isHoveredOnMap(result)}
            />
          ))}
        </>
      )}
    </SelectionCard.Group>
  );
};
